import React from 'react';
import { connect } from 'react-redux';
import { map } from 'lodash';
import HeaderMetaConfiguration from 'js/components/destination/HeaderMetaConfiguration/HeaderMetaConfiguration';
import { convertTextToMentionsState } from 'js/lib/DraftJSUtils';
import JSONUtils from '../../../lib/JSONUtils';
import DestinationPayloadContainer from './DestinationPayloadContainer';
import RestConfigurationSection from './RestConfigurationSection';
import { getCurrentOrg } from 'js/reducers/session';
import { SUPPORT_KEY_SINK_FORM_REST } from 'js/constants/supportPageKeys';
import { BATCH_API_TEST_ENABLED } from 'js/constants/config';
import { ResourcePopoverMenu } from 'src/new/elements/ResourcePopoverMenu';
import { UiMode } from 'new/constants/uiMode';

import './RestConfigure.scss';
import { tryCatchAsync } from 'new/utils/TryCatch';

const getMentionsFromSchema = (dataSchema) => {
  return map(dataSchema, (attr) => ({
    name: `{${attr.__name}}`,
    display_name: attr.__name,
    type: 'attr',
    type_display: 'Data Attributes',
  }));
};

const getStartingState = (props) => {
  const { dataSchema, sinkConfig } = props;
  const url = (sinkConfig && sinkConfig['url.template']) || '';
  const urlSuggestions = getMentionsFromSchema(dataSchema);
  const urlTemplateEditorState = convertTextToMentionsState(url, urlSuggestions);

  return {
    urlSuggestions,
    urlTemplateEditorState,
    filteredURLSuggestions: urlSuggestions,
  };
};

class RestConfigure extends React.Component {
  constructor(props) {
    super(props);
    this.state = getStartingState(props);
  }

  componentDidMount() {
    if (this.props.setContextualSupport) {
      this.props.setContextualSupport(SUPPORT_KEY_SINK_FORM_REST);
    }
  }

  componentDidUpdate = (prevProps) => {
    if (this.props.dataSchema !== prevProps.dataSchema) {
      const { urlTemplateEditorState, urlSuggestions, filteredURLSuggestions } = getStartingState(
        this.props,
      );

      this.setState({
        urlTemplateEditorState,
        urlSuggestions,
        filteredURLSuggestions,
      });
    }
  };

  componentWillUnmount = () => {
    this.props.onSinkMappingUpdated(null, true);
    if (this.props.unsetContextualSupport) {
      this.props.unsetContextualSupport(SUPPORT_KEY_SINK_FORM_REST);
    }
  };

  onChange = ({ name, value }) => {
    if (name && name !== '') {
      const { sinkConfig } = this.props;
      const data = { ...sinkConfig, [name]: value };
      this.props.onSinkConfigChanged(data);
    }
  };

  onSaveAdvancedMode = async (sinkConfigString) => {
    const { onSaveAdvancedMode, nextFormStage, onError } = this.props;

    if (!JSONUtils.isJSON(sinkConfigString)) {
      onError('The provided JSON is invalid. Please check the syntax and try again.');
      return;
    }

    const { name, description, sink_config } = JSON.parse(sinkConfigString);

    const data = {
      ...sink_config,
      name,
      description,
      'ui.mode': UiMode.CODE,
    };

    const { error } = await tryCatchAsync(onSaveAdvancedMode(data));
    if (error) {
      onError(error.message);
      return;
    }

    nextFormStage();
  };

  onSwitchUiMode = () => {
    const { sinkConfig, onSinkConfigChanged } = this.props;
    if (typeof onSinkConfigChanged !== 'function') return;

    onSinkConfigChanged({ ...sinkConfig, 'ui.mode': UiMode.VIEW });
  };

  getResourceConfig = () => {
    const { addedDataSink, sinkConfig } = this.props;
    const clonedSinkConfig = structuredClone(sinkConfig);
    const name = addedDataSink?.name || clonedSinkConfig?.name || '';
    const description = addedDataSink?.description || clonedSinkConfig?.description || '';

    delete clonedSinkConfig?.name;
    delete clonedSinkConfig?.description;
    return {
      name,
      description,
      sink_config: clonedSinkConfig,
    };
  };

  render() {
    const {
      paging,
      currentOrg,
      selectedConnection,
      addedDataSink,
      dataSchema,
      sinkConfig,
      setContextualSupport,
      unsetContextualSupport,
      onSinkMappingUpdated,
      onSinkConfigChanged,
      formData,
      onToggleDestinationDraft,
    } = this.props;

    const { groupingAlgo, payloadFieldName, otherProps } = this.state;

    const isBatchMode = !!sinkConfig['batch.mode'];

    const requestPayloadDescription =
      isBatchMode === true
        ? "This endpoint has been configured to group multiple samples into one API call to meet the API vendor's requirements. The samples below are a representation of samples used to generate the batched request, and not the actual API request payload itself."
        : 'Payloads for some sample requests that will be made to this API endpoint. You can change the payload by updating the output attributes of this Nexset.';
    const payloadPanelHeader = isBatchMode === true ? 'Sample for Payload' : 'Sample Payload';

    const { dataSet: nexset } = this.props;
    const sinkName = sinkConfig?.name ?? addedDataSink?.name ?? '';
    const descValue = sinkConfig?.description ?? addedDataSink?.description ?? '';
    const disableTestingTab = isBatchMode && !BATCH_API_TEST_ENABLED;

    const resourceConfig = this.getResourceConfig();
    const selectedCredentialId = formData?.selectedCredentialId ?? this.props.selectedCredentialId;

    return (
      <>
        <HeaderMetaConfiguration
          nexsetId={nexset?.id}
          isDestination
          name={sinkName}
          description={descValue}
          connection={selectedConnection}
          onChange={this.onChange}
          nexsetChipChildren={
            <ResourcePopoverMenu
              isDestination
              nexsetId={nexset?.id}
              resourceConfig={resourceConfig}
              connectorType={selectedConnection}
              jsonConfigOnSave={this.onSaveAdvancedMode}
              jsonConfigOnClose={this.props.goBack}
              onSwitchUiMode={this.onSwitchUiMode}
              disabledActions={['view-json-config']}
              resourcePersisted={!!addedDataSink?.id}
            />
          }
          sourceOrDestinationChipChildren={
            <ResourcePopoverMenu
              isDestination={!!sinkConfig}
              resourceConfig={resourceConfig}
              connectorType={selectedConnection}
              jsonConfigOnSave={this.onSaveAdvancedMode}
              jsonConfigOnClose={this.props.goBack}
              onSwitchUiMode={this.onSwitchUiMode}
              disabledActions={['view-nexset-details']}
              resourcePersisted={!!addedDataSink?.id}
            />
          }
        />
        <div className="configure-api configure-template rest-configure">
          <div className="rest-configure-left-column">
            <section className="configuration-holder">
              <RestConfigurationSection
                paging={paging}
                currentOrg={currentOrg}
                selectedConnection={selectedConnection}
                addedDataSink={addedDataSink}
                dataSchema={dataSchema}
                sinkConfig={sinkConfig}
                setContextualSupport={setContextualSupport}
                unsetContextualSupport={unsetContextualSupport}
                onSinkMappingUpdated={onSinkMappingUpdated}
                onSinkConfigChanged={onSinkConfigChanged}
              />
            </section>
          </div>
          <div className="rest-configure-right-column">
            <DestinationPayloadContainer
              heading="Sample Request Payloads"
              description={requestPayloadDescription}
              panelHeading={payloadPanelHeader}
              groupingAlgo={groupingAlgo}
              payloadFieldName={payloadFieldName}
              otherProps={JSONUtils.isJSON(otherProps) ? otherProps : '{}'}
              selectedCredentialId={selectedCredentialId}
              dataSchema={dataSchema}
              selectedConnection={selectedConnection}
              onToggleDestinationDraft={onToggleDestinationDraft}
              disableTestingTab={disableTestingTab}
              parentDataSet={this.props.parentDataSet}
              parentDataSetSamples={this.props.parentDataSetSamples}
            />
          </div>
        </div>
      </>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const { formType } = ownProps;
  const { paging, forms } = state;
  const formData = forms[formType] ? forms[formType].data : {};

  return {
    paging,
    currentOrg: getCurrentOrg(state),
    sinkConfig: formData?.sinkConfig,
  };
};

export default connect(mapStateToProps)(RestConfigure);
